import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const LINKS = [["/radio", "Radio"], ["/artists", "Artists"], ["/charts", "Weekly Chart"], ["/battle", "Battle"], ["/weekly", "Recap"], ["/blog", "Magazine"], ["/pricing", "Plans"]];

export const Logo = () => (
  <Link to="/" data-testid="nav-logo" className="flex items-center gap-2">
    <span className="relative flex h-7 w-7 items-center justify-center rounded-full border-2 border-[#FF2E63]">
      <span className="h-2 w-2 rounded-full bg-[#00F5D4]" />
    </span>
    <span className="font-display text-lg font-black tracking-[.18em]">FUORIONDA</span>
  </Link>
);

export const Navbar = () => {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const doLogout = async () => { await logout(); nav("/"); };
  const account = user ? (user.role === "admin" ? ["/admin", "Admin"] : ["/dashboard", "Dashboard"]) : ["/login", "Log in"];
  const cls = ({ isActive }) => `font-display text-sm font-medium transition-colors ${isActive ? "text-[#00F5D4]" : "text-slate-300 hover:text-white"}`;
  return (
    <header className="glass fixed inset-x-0 top-0 z-50 border-x-0 border-t-0">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 md:px-8">
        <Logo />
        <nav className="hidden items-center gap-7 md:flex">
          {LINKS.map(([to, l]) => <NavLink key={to} to={to} className={cls} data-testid={`nav-${l.toLowerCase().replace(/ /g, "-")}`}>{l}</NavLink>)}
        </nav>
        <div className="hidden items-center gap-3 md:flex">
          <NavLink to={account[0]} className={cls} data-testid="nav-account">{account[1]}</NavLink>
          {!user && <Link to="/join" data-testid="nav-join-invite-link" className="btn-hot !px-5 !py-2 text-sm">Join free</Link>}
          {user && <button onClick={doLogout} data-testid="logout-button" className="flex items-center gap-1.5 font-display text-sm text-slate-400 transition-colors hover:text-[#FF2E63]"><LogOut size={15} />Log out</button>}
        </div>
        <button className="md:hidden" data-testid="nav-mobile-toggle" onClick={() => setOpen(!open)}>{open ? <X /> : <Menu />}</button>
      </div>
      {open && (
        <nav className="flex flex-col gap-4 border-t border-white/5 px-6 py-6 md:hidden" onClick={() => setOpen(false)}>
          {[...LINKS, account].map(([to, l]) => <NavLink key={to} to={to} className={cls} data-testid={`mnav-${l.toLowerCase().replace(/ /g, "-")}`}>{l}</NavLink>)}
          {!user && <Link to="/join" className="btn-hot" data-testid="mnav-join">Join free</Link>}
          {user && <button onClick={doLogout} data-testid="mnav-logout-button" className="flex items-center gap-2 font-display text-sm text-slate-400 hover:text-[#FF2E63]"><LogOut size={15} />Log out</button>}
        </nav>
      )}
    </header>
  );
};

export const Footer = () => (
  <footer className="border-t border-white/5 px-4 pb-32 pt-14 md:px-8">
    <div className="mx-auto flex max-w-7xl flex-col justify-between gap-8 md:flex-row">
      <div className="max-w-sm">
        <Logo />
        <p className="mt-4 text-sm text-slate-500">The 24/7 radio and showcase for independent artists. Curated, blind-judged, backed by real numbers.</p>
      </div>
      <div className="grid grid-cols-2 gap-x-12 gap-y-2 text-sm text-slate-400">
        {[["/join", "Join as artist"], ["/pricing", "Plans & services"], ["/charts", "Weekly Chart"], ["/weekly", "Weekly Recap"], ["/battle", "Battle"], ["/contact", "Contact"], ["/terms", "Terms"], ["/privacy", "Privacy"], ["/cookies", "Cookies"], ["/blog", "Magazine"], ["/artists", "Artists"], ["/radio", "Radio"]].map(([to, l]) => (
          <Link key={to} to={to} className="hover:text-[#00F5D4]">{l}</Link>
        ))}
      </div>
    </div>
    <div className="mx-auto mt-10 max-w-7xl font-mono text-[10px] uppercase tracking-widest text-slate-400">© {new Date().getFullYear()} Fuorionda — made in Italy for independent music</div>
  </footer>
);
